// Breadcrumb trail (nav-redesign PR 9). Rendered once by PageLayout from
// the current pathname — see breadcrumb-helpers.ts for the pure logic.
//
// 2026-10-13 (Grant: "navigating back to certain places in the OS is
// annoying"), three additions so going back is one click from anywhere:
//   1. every step that is a real page is a link (not just a dozen
//      top-level ones), and "Admin" opens the Admin menu;
//   2. a record step shows the record's name, not "Detail", and a page
//      can supply a better trail than its address (usePageTrail);
//   3. "← Back to <the page you came from>" leads the bar whenever you
//      arrived from another OS page, unless the page has its own
//      BackLink in its header.

import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import type { RoleAwareUser } from "../utils/role-visibility";
import { canSeeAdminSection } from "../utils/role-visibility";
import { breadcrumbForPath, type Crumb } from "./breadcrumb-helpers";
import { useChrome } from "./ChromeContext";
import { useNavHistory } from "./nav-history";

// The left rail is hidden at this width (styles.css, `@container vp
// (max-width: 500px)`), so the Admin menu lives in the phone menu drawer.
const PHONE_MQ = "(max-width: 500px)";

function isPhoneWidth(): boolean {
  try {
    return window.matchMedia?.(PHONE_MQ)?.matches ?? false;
  } catch {
    return false;
  }
}

export function Breadcrumbs({ pathname, user }: { pathname: string; user?: RoleAwareUser }) {
  const history = useNavHistory();
  const chrome = useChrome();
  const navigate = useNavigate();

  const crumbs: Crumb[] = history?.pageTrail
    ? [{ label: "Home", to: "/" }, ...history.pageTrail]
    : breadcrumbForPath(pathname, { currentName: history?.currentName ?? null });

  // The back link earns its place only when it says something the trail
  // doesn't: if the page you came from is exactly the trail's parent step,
  // that step already IS the way back.
  const previous = history?.previous ?? null;
  const parent = crumbs.length >= 2 ? crumbs[crumbs.length - 2] : null;
  const showBack =
    previous !== null && (history?.ownBackLinks ?? 0) === 0 && previous.href !== parent?.to;

  // Home-only (or empty) trail with nothing to go back to — render nothing.
  if (crumbs.length <= 1 && !showBack) return null;

  const canOpenAdmin = user ? canSeeAdminSection(user) : false;
  const openAdminMenu = () => {
    if (isPhoneWidth()) {
      chrome.openMobileNav();
    } else if (chrome.activeNavSection !== "admin") {
      chrome.toggleNavSection("admin");
    }
  };

  const goBack = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    if (previous) void navigate(-previous.steps);
  };

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {showBack && previous && (
        <a href={previous.href} onClick={goBack} className="breadcrumbs-back">
          ← Back to {previous.label}
        </a>
      )}
      {crumbs.length > 1 && (
        <ol className="breadcrumbs-list">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={`${c.label}-${i}`} className="breadcrumbs-item">
                {c.to && !last ? (
                  <Link to={c.to} className="breadcrumbs-link">
                    {c.label}
                  </Link>
                ) : c.opensAdminMenu && canOpenAdmin && !last ? (
                  <button
                    type="button"
                    className="breadcrumbs-link breadcrumbs-button"
                    onClick={openAdminMenu}
                    title="Open the Admin menu"
                  >
                    {c.label}
                  </button>
                ) : (
                  <span
                    className="breadcrumbs-current"
                    aria-current={last ? "page" : undefined}
                  >
                    {c.label}
                  </span>
                )}
                {!last && (
                  <span className="breadcrumbs-sep" aria-hidden="true">
                    ›
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
}
